import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router";

import { bindActionCreators, Dispatch } from "redux";

import { ConfirmDialog, Overlay, ActionBar } from "@plex/react-components";

import { commonMessages } from "infrastructure/localization";
import { useLocalizedValues, useUserContext } from "ui/hooks";

import MessageRelationshipsForm from "./form/MessageRelationshipsForm";
import { messageRelationshipsActions } from "./MessageRelationships.actions";
import { IMessageRelationshipsState, IRelationship } from "./MessageRelationships.state";

interface IMessageRelationshipsProps {
  messageId: string;
  state: IMessageRelationshipsState;
  loadRelationships: (messageId: string) => void;
  loadMessageDetails: (messageId: string) => void;
  deleteRelationship: (relationship: IRelationship) => void;
}

const MessageRelationships: React.FC<IMessageRelationshipsProps> = ({ messageId, state, loadRelationships, loadMessageDetails, deleteRelationship }) => {
  const localizedTitles = useLocalizedValues(() => ({
    question: commonMessages.info("deleteRecordQuestion"),
    askTitle: commonMessages.title("confirmationDialog"),
    updateButtonText: commonMessages.action("update"),
    deleteButtonText: commonMessages.action("delete"),
    noButton: commonMessages.option("no"),
    yesButton: commonMessages.option("yes")
  }));

  const history = useHistory();
  const userContext = useUserContext();
  const roles = userContext.getRoleAccessData();
  const [isDialogOpened, setIsDialogOpened] = useState(false);

  useEffect(() => {
    if (state.shouldRedirect) {
      history.goBack();
    }
  }, [state.shouldRedirect, history]);

  useEffect(() => {
    loadRelationships(messageId);
    loadMessageDetails(messageId);
  }, [loadRelationships, loadMessageDetails, messageId]);

  const onDeleteDialogClick = useCallback(() => {
    setIsDialogOpened(true);
  }, [setIsDialogOpened]);

  const answers = [localizedTitles.yesButton, localizedTitles.noButton];
  const onDialogHide = useCallback(
    (answer: string) => {
      setIsDialogOpened(false);
      if (answer === localizedTitles.yesButton) {
        deleteRelationship({
          messageId: messageId,
          relationshipMessageId: state.selectedRelationship[0] && state.selectedRelationship[0].messageId,
          isParentMessage: state.messageDetails.isParentMessage,
          messageVersion: state.messageDetails.version,
          relationshipMessageVersion: state.selectedRelationship[0] && state.selectedRelationship[0].version
        });
      }
    },
    [deleteRelationship, messageId, state.selectedRelationship, state.messageDetails, localizedTitles]
  );

  return (
    <>
      <Overlay show={state.isLoading} showSpinner />
      <ActionBar>
        {roles.PlexTechnicalWriter && (
          <ActionBar.Button
            id="deleteButton"
            text={localizedTitles.deleteButtonText}
            title={localizedTitles.deleteButtonText}
            disabled={state.selectedRelationship.length === 0}
            onClick={onDeleteDialogClick}
          />
        )}
      </ActionBar>
      <MessageRelationshipsForm relationships={state.relationships} isParentMessageId={state.messageDetails && state.messageDetails.isParentMessage} />
      <ConfirmDialog
        show={isDialogOpened}
        onHide={onDialogHide}
        question={localizedTitles.question}
        title={localizedTitles.askTitle}
        defaultAnswer={answers[1]}
        answers={answers}
      ></ConfirmDialog>
    </>
  );
};

export default connect(
  (state: { messageRelationships: IMessageRelationshipsState }) => {
    return {
      state: state.messageRelationships
    };
  },
  (dispatch: Dispatch) =>
    bindActionCreators(
      {
        loadRelationships: messageRelationshipsActions.loadRelationships,
        loadMessageDetails: messageRelationshipsActions.loadMessageDetails,
        deleteRelationship: messageRelationshipsActions.deleteRelationship
      },
      dispatch
    )
)(MessageRelationships);
